import { Night, Token } from "@mixitone/models";
import type { AppController } from "controllers/AppController";
import tauri from "lib/tauri";
import { messagesService } from "services";
import routeService from "services/RouteService";

/**
 * Global service to handle NFC registration logic throughout the application
 */
class NfcRegistrationService {
  private controller: AppController;
  enabled = true;
  private inFlightUids = new Set<string>();

  constructor(controller: AppController) {
    this.controller = controller;

    tauri.listen<{ uid: string }>("plugin-cardreader-uid", (event) => {
      void this.handleCardRead(event.payload.uid);
    });
  }

  /**
   * Main entry point for NFC card detection
   */
  async handleCardRead(uid: string): Promise<void> {
    if (!this.enabled) return;
    if (this.inFlightUids.has(uid)) return;
    this.inFlightUids.add(uid);

    try {
      console.log("NFC card detected with UID:", uid);

      const token = await Token.findOrCreate(uid, "nfc");
      const currentClub = this.controller.state.currentClub;
      const activeSessions = await Night.active().all();
      const clubPlayers = await token.allClubPlayers();
      const modalOpen = this.controller.isModalOpen();

      if (currentClub) {
        const clubPlayer = clubPlayers.find((p) => p.club_id === currentClub.uniqueId);
        const activeSession = activeSessions.find((session) => session.club_id === currentClub.uniqueId);

        if (!clubPlayer) {
          if (modalOpen) {
            console.log("Modal is already open, cannot register new player");
            // If a modal is open, we cannot proceed with registration
            messagesService.addMessage("Please wait...", "warning", "nope");
            return;
          }
          console.log("No club player found, opening new player modal");

          // We can show the new player modal
          routeService.openModal("/clubs/:clubId/players/new", {
            params: { clubId: currentClub.id!, tokenId: token.uniqueId, sessionId: activeSession?.uniqueId },
          });
          return;
        }

        if (activeSession) {
          if (activeSession.hadHadPlayerFor5Minutes(clubPlayer)) {
            await activeSession.togglePlayer(clubPlayer);
          } else {
            await activeSession.addClubPlayer(clubPlayer);
          }

          return;
        }

        if (modalOpen) {
          messagesService.addMessage("Please wait...", "warning", "nope");
          return;
        }

        // No active session, show start session modal
        routeService.openModal("/clubs/:clubId/sessions/new", {
          params: { clubId: currentClub.uniqueId, clubPlayerId: clubPlayer.uniqueId },
        });
        return;
      }

      if (activeSessions.length > 0) {
        for (const activeSession of activeSessions) {
          const clubPlayer = clubPlayers.find((p) => p.club_id === activeSession.club_id);
          if (!clubPlayer) continue;

          await activeSession.addClubPlayer(clubPlayer);
          return;
        }
      }
    } finally {
      this.inFlightUids.delete(uid);
    }
  }

  /**
   * Enable/disable global NFC handling
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
}

export default NfcRegistrationService;
